import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Truck, RotateCcw, Globe, ShieldCheck, Clock, MapPin } from 'lucide-react';

const ShippingReturnsPage = () => {
  return (
    <div className="min-h-screen bg-[#050505] pt-28 pb-20" data-testid="shipping-returns-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <Link to="/" data-testid="shipping-back-link" className="inline-flex items-center gap-2 text-[#D4AF37] mb-8 hover:text-[#E5C158] transition-colors">
          <ArrowLeft size={18} />
          <span className="font-heading text-xs tracking-[0.2em]">BACK TO HOME</span>
        </Link>

        <div className="mb-12">
          <p className="font-arabic text-2xl text-[#D4AF37]/60 mb-2">الشحن والإرجاع</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-wide text-white mb-4">
            SHIPPING & <span className="text-[#D4AF37]">RETURNS</span>
          </h1>
          <p className="font-body text-white/50 text-base">
            Fast, tracked delivery across Australia. Hassle-free returns within 14 days.
          </p>
        </div>

        {/* SHIPPING */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <Truck className="text-[#D4AF37]" size={24} />
            <h2 className="font-heading text-2xl md:text-3xl tracking-wide text-white">SHIPPING</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="border border-[#D4AF37]/15 bg-[#0a0a0a] p-6">
              <MapPin className="text-[#D4AF37] mb-3" size={20} />
              <h3 className="font-heading text-sm tracking-[0.15em] text-white mb-2">AUSTRALIA WIDE</h3>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                Standard tracked shipping — 3–7 business days. Express available at checkout.
              </p>
            </div>
            <div className="border border-[#D4AF37]/15 bg-[#0a0a0a] p-6">
              <Clock className="text-[#D4AF37] mb-3" size={20} />
              <h3 className="font-heading text-sm tracking-[0.15em] text-white mb-2">SAME-DAY DISPATCH</h3>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                Orders placed before 2pm AEST (Mon–Fri) are dispatched the same day from our Sydney warehouse.
              </p>
            </div>
            <div className="border border-[#D4AF37]/15 bg-[#0a0a0a] p-6">
              <ShieldCheck className="text-[#D4AF37] mb-3" size={20} />
              <h3 className="font-heading text-sm tracking-[0.15em] text-white mb-2">SECURE PACKAGING</h3>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                Every bottle is double-packed with protective inserts to ensure pristine arrival.
              </p>
            </div>
            <div className="border border-[#D4AF37]/15 bg-[#0a0a0a] p-6">
              <Globe className="text-[#D4AF37] mb-3" size={20} />
              <h3 className="font-heading text-sm tracking-[0.15em] text-white mb-2">INTERNATIONAL</h3>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                We ship to select international destinations. Rates calculated at checkout. Customs fees may apply.
              </p>
            </div>
          </div>

          <div className="border-l-2 border-[#D4AF37]/40 pl-6 py-2">
            <p className="font-heading text-xs tracking-[0.2em] text-[#D4AF37] mb-2">SHIPPING RATES</p>
            <ul className="space-y-2 font-body text-sm text-white/70">
              <li>• Standard (3–7 days): A$12 — FREE on orders over A$150</li>
              <li>• Express (1–3 days): A$18</li>
              <li>• International: calculated at checkout based on destination</li>
            </ul>
          </div>
        </section>

        {/* RETURNS */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <RotateCcw className="text-[#D4AF37]" size={24} />
            <h2 className="font-heading text-2xl md:text-3xl tracking-wide text-white">RETURNS</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="font-heading text-sm tracking-[0.15em] text-[#D4AF37] mb-3">14-DAY RETURN WINDOW</h3>
              <p className="font-body text-white/65 text-sm leading-relaxed">
                We accept returns within 14 days of delivery. The product must be unused, unopened and in its original sealed packaging.
                Due to the nature of fragrances, opened bottles cannot be returned for hygiene reasons.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-sm tracking-[0.15em] text-[#D4AF37] mb-3">DAMAGED OR FAULTY ITEMS</h3>
              <p className="font-body text-white/65 text-sm leading-relaxed">
                If your order arrives damaged or faulty, email us within 48 hours at <span className="text-[#D4AF37]">info@qasr.com.au</span> with
                photos and your order number. We'll arrange a replacement or full refund immediately.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-sm tracking-[0.15em] text-[#D4AF37] mb-3">HOW TO RETURN</h3>
              <ol className="font-body text-white/65 text-sm leading-relaxed space-y-2 list-decimal list-inside">
                <li>Email <span className="text-[#D4AF37]">info@qasr.com.au</span> with your order number and reason for return</li>
                <li>We'll respond within 24 hours with return instructions and a shipping label if applicable</li>
                <li>Package the unopened item securely and post it back</li>
                <li>Once received and inspected, your refund is processed within 3–5 business days</li>
              </ol>
            </div>

            <div>
              <h3 className="font-heading text-sm tracking-[0.15em] text-[#D4AF37] mb-3">REFUND METHOD</h3>
              <p className="font-body text-white/65 text-sm leading-relaxed">
                Refunds are issued to the original payment method. Bank processing may take 5–10 business days to appear in your account.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-sm tracking-[0.15em] text-[#D4AF37] mb-3">SALE & DISCOUNTED ITEMS</h3>
              <p className="font-body text-white/65 text-sm leading-relaxed">
                Items purchased on sale or with a discount code are eligible for store credit only — no cash refunds.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center border-t border-[#D4AF37]/15 pt-12">
          <p className="font-arabic text-xl text-[#D4AF37]/50 mb-2">نحن هنا للمساعدة</p>
          <p className="font-heading text-sm tracking-[0.2em] text-white/70 mb-6">NEED HELP WITH AN ORDER?</p>
          <Link
            to="/contact"
            data-testid="shipping-contact-cta"
            className="inline-block px-8 py-3 border border-[#D4AF37] text-[#D4AF37] font-heading text-xs tracking-[0.2em] hover:bg-[#D4AF37] hover:text-[#050505] transition-colors duration-200"
          >
            CONTACT US
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ShippingReturnsPage;
